import type { ArtifactKind } from '@inference.engine/widgets';
import type { FilterChipOption, SortChipOption } from '@/shared/ui';
import { FilterChipPopover } from '@/shared/ui/FilterChipPopover';
import { SearchInput } from '@/shared/ui/SearchInput';
import { SortChipPopover } from '@/shared/ui/SortChipPopover';
import { ARTIFACT_KIND_FILTER_CHOICES } from '../../lib/artifact-kind-palette';
import { useTranslation } from 'react-i18next';

interface SortConfig {
  options: ReadonlyArray<SortChipOption>;
  value: string | null;
  onChange: (next: string | null) => void;
  resetOptionLabel?: string;
}

interface Props {
  searchInput: string;
  onSearchInputChange: (next: string) => void;
  searchAriaLabel: string;
  kindFilter: ArtifactKind | null;
  onKindFilterChange: (next: ArtifactKind | null) => void;
  /**
   * Type-filter choices. Defaults to every artifact kind
   * (`ARTIFACT_KIND_FILTER_CHOICES`); the project picker narrows this to
   * Briefs + the Library artifact kinds.
   */
  kindFilterOptions?: ReadonlyArray<FilterChipOption<ArtifactKind>>;
  /**
   * Optional sort chip. Omit for surfaces with a fixed server-side order
   * (e.g. the project picker's newest-first default).
   */
  sort?: SortConfig;
}

/**
 * Shared search + sort + Type-filter bar for the artifact pickers. Extracted
 * from `ExistingArtifactsPicker` so the project-scoped picker inherits the
 * identical chip visuals without re-implementing the toolbar.
 */
export function PickerToolbar(props: Props) {
  const { t } = useTranslation('common');

  return (
    <div className="flex items-center gap-2">
      <SearchInput
        placeholder={t('artifact.pickerToolbar.search')}
        value={props.searchInput}
        onChange={props.onSearchInputChange}
        aria-label={props.searchAriaLabel}
        className="w-80"
      />

      {props.sort && (
        <SortChipPopover
          options={props.sort.options}
          value={props.sort.value}
          onChange={props.sort.onChange}
          resetOptionLabel={props.sort.resetOptionLabel}
        />
      )}

      <FilterChipPopover<ArtifactKind>
        label={t('artifact.pickerToolbar.type')}
        value={props.kindFilter}
        onChange={props.onKindFilterChange}
        options={props.kindFilterOptions ?? ARTIFACT_KIND_FILTER_CHOICES}
        triggerAriaLabel={t('artifact.pickerToolbar.filterByType')}
        resetOptionLabel={t('artifact.pickerToolbar.allTypes')}
      />
    </div>
  );
}
